import { getPlan } from "../../api";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { QueryKey } from "./types";
import { parsePlanLongIdIntoUrlPartForApi } from "../objectUtilities";
import { isValidApiLongPlanId } from "../planHelpers";
import { useLIS } from "../context-hooks/useLis";
import type { Plan, PlanLongId } from "../../@types";
import { useMemo } from "react";
import { useCurrentPlanYear } from "../yearFlagHelpers";
import { useRouteParams } from "../routing-hooks/useRouteParams";
import { useSearchParams } from "../routing-hooks/useSearchParams";
import { useBackendVersions } from "./useBackendVersions";
import { useAppState } from "../context-hooks/useAppState";
import { getProviderNetworkFailure } from "../providerNetworkHelpers";

export interface UsePlanPayload {
  /** a null value indicates a successful endpoint call that fails the typeguard */
  plan?: Plan | null;
  isLoading: boolean;
  error?: Error;
  prefetchPlan: () => void;
  providerNetworkFailure: boolean;
}

type UsePlanType = (overrides?: UsePlanOverrides) => UsePlanPayload;

type UsePlanOverrides = {
  /** a specific planId, otherwise, we look at the route */
  planId?: PlanLongId;
  /** a specific year to get LIS for (current or next plan year) */
  lisYear?: number;
  /** prevents the query from being called, useful for if you just want the pre-fetch function */
  disabled?: boolean;
  planPreviewHeader?: string;
};

/**
 * query hook which fetches a plan
 * @example const plan = usePlan(); // will grab the plan based of the planId in the route
 * @example const plan = usePlan({planId}); // will grab a specific plan
 */
export const usePlan: UsePlanType = (overrides?: UsePlanOverrides) => {
  // * Context
  const { getLISForYear } = useLIS();
  const { providers, zipcode } = useAppState();

  // * Flags
  const mctCurrentPlanYear = useCurrentPlanYear();

  // * Route
  const { planId: routePlanId } = useRouteParams();
  const { selectedYear } = useSearchParams();

  // * Queries
  const { planDbTimestamp, staleTime } = useBackendVersions();

  // * Constants
  const planLongId = overrides?.planId || routePlanId;
  const apiPlanId = planLongId
    ? parsePlanLongIdIntoUrlPartForApi(planLongId)
    : "";

  const planIdIsValid = isValidApiLongPlanId(apiPlanId);
  const enabled = planIdIsValid && !overrides?.disabled;

  /** will use route `year` as a fallback and `mctCurrentPlanYear` if it does not exist */
  const lisToSend = getLISForYear(
    overrides?.lisYear || selectedYear || mctCurrentPlanYear
  );

  const providerNpis = providers.map(p => p.npi);

  // * Queries
  const queryClient = useQueryClient();

  const planOptions = queryOptions({
    queryKey: [
      QueryKey.Plan,
      planDbTimestamp,
      apiPlanId,
      lisToSend,
      overrides?.planPreviewHeader,
      providerNpis,
      zipcode,
    ],
    queryFn: () => {
      const queryParams: {
        lis?: string;
        providers?: string[];
        zip?: string;
      } = {};
      if (lisToSend) {
        queryParams.lis = lisToSend;
      }
      // @TODO - decide whether it's safe to strip out checks on things that are
      // theoretically always defined (`providerNpis`)
      if (!!providerNpis && providerNpis.length > 0 && !!zipcode) {
        queryParams.providers = providerNpis;
        queryParams.zip = zipcode;
      }
      return getPlan({
        id: apiPlanId,
        queryParams,
        ...(overrides?.planPreviewHeader && {
          headers: { "hpms-plan-preview": overrides?.planPreviewHeader },
        }),
      });
    },
  });

  const {
    data,
    error: queryError,
    isLoading,
  } = useQuery({
    ...planOptions,
    enabled,
    staleTime,
  });

  const error = useMemo(() => {
    // error comes from calling the endpoint and not getting a plan back
    if (queryError) return queryError as Error;
    if (!planIdIsValid) {
      // error comes from invalid plan id, where we do not call the endpoint in the first place
      return new Error(`Invalid plan ID (${apiPlanId})`);
    }
    return;
  }, [apiPlanId, planIdIsValid, queryError]);

  const prefetchPlan = () => {
    queryClient.prefetchQuery(planOptions);
  };

  return {
    plan: data?.plan,
    isLoading,
    error,
    prefetchPlan,
    providerNetworkFailure: getProviderNetworkFailure(data?.headers),
  };
};
